"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border rounded-md bg-card text-card-foreground">
        <p className="font-serif text-xl text-white">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="border-b border-border pb-2">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Alexander Sterling" {...field} className="border-0 border-b border-transparent bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-serif text-lg text-white placeholder:text-slate-muted/60 h-11 rounded-md" />
            </FormControl>
            <FormDescription className="font-sans text-xs text-slate-muted">
              Legal name for documentation
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="border-b border-border pb-2">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="a.sterling@investment.com" {...field} className="border-0 border-b border-transparent bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-serif text-lg text-white placeholder:text-slate-muted/60 h-11 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="border-b border-border pb-2">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">
              Property or Resort Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Cabo Luxury Villas" {...field} className="border-0 border-b border-transparent bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-serif text-lg text-white placeholder:text-slate-muted/60 h-11 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="podcast_title" rules={{
      required: "Asset type is required"
    }} render={({
      field
    }) => <FormItem className="border-b border-border pb-2">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">
              Asset Type
            </FormLabel>
            <FormControl>
              <Input placeholder="Fractional Ownership / Timeshare" {...field} className="border-0 border-b border-transparent bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-serif text-lg text-white placeholder:text-slate-muted/60 h-11 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="start_date" render={({
      field
    }) => <FormItem className="border-b border-border pb-2">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">
              Preferred Consultation Date
            </FormLabel>
            <FormControl>
              <Input type="date" {...field} className="border-0 border-b border-transparent bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-serif text-lg text-white placeholder:text-slate-muted/60 h-11 rounded-md" />
            </FormControl>
            <FormDescription className="font-sans text-xs text-slate-muted">
              Select a date for your portfolio review
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="border-b border-border pb-2">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">
              Portfolio Objectives
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your management needs or exit requirements..." rows={4} {...field} className="border-0 border-b border-transparent bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-serif text-lg text-white placeholder:text-slate-muted/60 min-h-[120px] resize-none rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md bg-destructive/10">
          <p className="text-destructive font-sans text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-colors duration-500 font-default text-xs uppercase tracking-[0.25em]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : "Request Elite Assessment"}
      </Button>
    </Form>;
}
export default function InvestorInsights() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const contactMeta = [{
    label: "Headquarters",
    value: "Av. Quintas California Mz 2 Lot 11, Los Cabos"
  }, {
    label: "Direct Line",
    value: "+52 624 164 7031"
  }, {
    label: "Inquiries",
    value: "imielitecabo@gmail.com"
  }, {
    label: "Compliance",
    value: "PROFECO & SAT Registered"
  }];
  return <section data-section-id="3294" id="investor-insights" className="dark relative overflow-hidden bg-background py-24 md:py-32 lg:py-40 text-white" style={{
    backgroundImage: "radial-gradient(circle at 20% 30%, hsl(38 75% 55% / 0.05) 1px, transparent 1px), radial-gradient(circle at 80% 70%, hsl(38 75% 55% / 0.03) 1px, transparent 1px)",
    backgroundSize: "48px 48px, 36px 36px"
  }}>
      {}
      <div ref={headlineRef} aria-hidden="true" className={`pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-serif italic font-light whitespace-nowrap select-none" style={{
        fontSize: "clamp(5rem, 18vw, 20rem)",
        WebkitTextStroke: "1px hsl(38 75% 55% / 0.2)",
        color: "transparent",
        letterSpacing: "-0.04em",
        lineHeight: 1
      }}>
          Elite Management.
        </h2>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-8 lg:col-start-1">
            <div className="max-w-[540px] mx-auto lg:mx-0">
              <div className="mb-8 flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-primary">
                  05 / Investor Relations
                </span>
                <div className="h-px w-12 bg-primary" />
              </div>

              <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-white mb-12">Secure your legacy with architectural precision. Whether optimizing rental yields or executing a compliant exit strategy, our 29 years of resort expertise ensures your assets are managed with absolute discretion.</p>

              <FormProvider formKey="investor_insights_form_real_estate" formId="investor_insights_form_real_estate" sectionName="investor-insights" defaultValues={{
              name: "",
              email: "",
              company: "",
              podcast_title: "",
              start_date: "",
              details: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              company: {
                required: false
              },
              podcast_title: {
                required: true
              },
              start_date: {
                required: false
              },
              details: {
                required: false
              }
            }} submitText="Request Elite Assessment" successMessage="Your inquiry has been received. An IMI Elite advisor will contact you within 24 hours." errorMessage="Transmission error. Please contact our Los Cabos office directly.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>

          {}
          <aside className="lg:col-span-4 lg:col-start-9 lg:pt-2">
            <div className="lg:sticky lg:top-24 max-w-[540px] mx-auto lg:mx-0">
              <div className="mb-6">
                <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-primary">
                  The Registry
                </span>
              </div>

              <div className="space-y-0">
                {contactMeta.map((item, i) => <div data-index={i} key={item.label} className="py-5 border-b border-border first:border-t">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-slate-muted mb-2">
                      {item.label}
                    </div>
                    <div className="font-default font-medium text-base text-white leading-snug">
                      {item.value}
                    </div>
                  </div>)}
              </div>

              <Separator className="my-8 bg-border" />

              <p className="font-serif italic text-sm text-slate-muted leading-relaxed">
                IMI Elite Management operates as a private consultancy. All portfolio reviews are conducted under strict confidentiality agreements to protect your financial privacy.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}